<template>
  <mailbox-verification-layout :name="mailboxName">
    <el-card>
      <template #header>
        <h3>Set up your outgoing email address</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <div v-else class="mailbox-verification-outgoing-email">

        <!-- Auto-forwarded email verified as outgoing email address -->
        <template v-if="isCustomFromAddressSameAsForwardingAddress">
          <p>
            <strong>Congratulations, your mailbox is ready to receive and send email!</strong>
          </p>
          <p><br></p>
          <email-client-figure
            :show-edit-button="false"
            :from-name="outgoingEmailSettings.outgoingFromEmail.name"
            :from-mail="outgoingEmailSettings.outgoingFromEmail.emailAddress"
            class="mailbox-verification-outgoing-email__email-client-figure"
          />
          <p>
            You can continue customizing other parts of this mailbox: 
          </p>
          <p><br></p>
          <ul>
            <li><strong>Add team members</strong> to this mailbox.</li>
            <li>Set up <strong>DKIM and Return-Path DNS records</strong> to ensure better email delivery.</li>
            <li>Configure <strong>helpful rules</strong>  around conversations and replies.</li>
          </ul>
          <p class="small">
            <strong>Note:</strong> You can always customize these things later by clicking the <i class="ddi ddi-ellipsis" /> option on your 
            mailbox home.
          </p>
          <p><br></p>
          <div class="outgoing-email-options">
            <dd-button 
              :color="ColorAssistant.MailboxColor"
              size="large" 
              @click="goToMailboxAssignPeople()"
            >Yes, continue customizing!</dd-button>
            <dd-button
              size="large"
              type="text"
              @click="goToMailbox()">Not now. Just take me to my new mailbox.</dd-button>
          </div>
        </template>

        <!-- Auto-forwarded email is public domain. Cannot make into outgoing email address. -->
        <template v-else-if="emailIsPublicDomain">
          <div class="public-domain-alert">
            <i class="ddi ddi-warning" />
            <p>Woops! We cannot set <strong>{{ forwardingFromAddress }}</strong> as the outgoing email address.</p>
          </div>
          <p><br></p>
          <p>
            This email address belongs to a <strong>public domain</strong> (e.g. gmail.com, outlook.com, etc.).
            We are not allowed to send emails from a domain name you don’t own 
            because this can be viewed as email spoofing.
          </p>
          <p><br></p>
          <p>
            While your emails will forward into DoneDone, when you reply to customers, emails will be sent as shown below:
          </p>
          <p><br></p>
          <email-client-figure
            :show-edit-button="false"
            :from-name="outgoingEmailSettings.outgoingFromEmail.name"
            :from-mail="outgoingEmailSettings.outgoingFromEmail.emailAddress"
            class="mailbox-verification-outgoing-email__email-client-figure"
          />
          <div class="outgoing-email-options">
            <dd-button 
              :color="ColorAssistant.MailboxColor"
              size="large" 
              @click="goToMailboxOutgoingEmail()"
            >Set up your outgoing email with a domain you own.</dd-button>
            <dd-button
              size="large"
              type="text"
              @click="goToMailbox()">Not now. Just take me to my new mailbox.</dd-button>
          </div>
        </template>

        <!-- Polling... -->
        <template v-else-if="pollingForOutgoingFromEmailVerification">
          <div class="postmark-verification">
            <h3>Verifying your address with <img src="./logos/postmark-logo.svg"></h3>
            <p v-if="postmarkUrlClicked">
              Thanks! Your outgoing email setup should be verified in just a few more seconds!
            </p>
            <p v-else>
              We're now connecting to our email partners at <a href="https://www.postmarkapp.com" class="just-a-link" target="_blank">Postmark</a>
              to allow you to send emails from DoneDone using <strong>{{ forwardingFromAddress }}</strong>.
            </p>
            <template v-if="hasPostmarkSenderSignatureConfirmationUrl && !postmarkUrlClicked">
              <p><br></p>
              <i class="ddi ddi-arrow-down bouncy" />
              <div class="confirmation-link-wrapper">
                <h4><a :href="postmarkSenderSignatureConfirmationUrl" class="just-a-link" target="_blank" @click="postmarkUrlClicked=true">Open link to complete verification with Postmark</a></h4>
                <p>(Then, come right back here).</p>
              </div>
            </template>
          </div>
          <verification-loader />
        </template>
        <template v-else-if="pollingFailed">
          <p>
            Hmm. We're having an issue setting up your outgoing email address.
          </p>
          <p><br></p>
          <ul>
            <li><a class="just-a-link" href="#" @click.stop.prevent="setOutgoingEmailAndAwaitVerification()">Try again</a>.</li>
            <li>If this persists, <a href="https://www.donedone.com/help-center" target="_blank" class="just-a-link">reach out to us</a> so we can help.</li>
          </ul>
        </template>

        <!-- Set up -->
        <template v-else>
          <p>
            Now that emails are forwarding from <strong>{{ forwardingFromAddress }}</strong>, 
            let's set this address as your outgoing ("From") address. Enter the name as you want it to appear in outgoing emails:
          </p>
          <template v-if="hasCustomFromAddress">
            <p><br></p>
            <p>
              Your current <strong>Outgoing Email Address</strong> is <strong>{{ outgoingEmailSettings.outgoingFromEmail.emailAddress}}</strong>.
              This will be replaced.
            </p>
          </template>
          <el-form
            ref="mailboxForm"
            :model="outgoingFromAddressData"
            label-position="top"
            class="mailbox-verification-outgoing-email__form"
          >
            <el-form-item prop="fromName">
              <el-input v-model="outgoingFromAddressData.fromName" :disabled="isSubmitting" placeholder="e.g. Support Staff" />
            </el-form-item>
            <email-client-figure
              :from-name="outgoingFromAddressData.fromName"
              :from-mail="outgoingFromAddressData.fromEmailAddress"
              :show-edit-button="false"
            />
            <dd-button
              :is-loading="isSubmitting"
              :is-disabled="disableSubmit"
              :color="ColorAssistant.MailboxColor"
              full-width
              @click="setOutgoingEmailAndAwaitVerification()"
            >
              Set outgoing email to {{outgoingFromAddressData.fromEmailAddress}}
            </dd-button>
            <p><br></p>
            <p class="small">
              <strong>Note:</strong> To set up a different outgoing email address, skip this step and go to
              <router-link :to="RouteAssistant.getMailboxSettingsOutgoingEmailRoute(accountID, mailboxID)" class="just-a-link">Outgoing Email Settings</router-link>.
            </p>
          </el-form>
        </template>
      </div>
    </el-card>
  </mailbox-verification-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxVerificationLayout from '@/layouts/mailbox-verification-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import EmailClientFigure from '@/components/global/email-client-figure.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IMailboxesOutputOutgoingEmailSettings } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import { IMailboxesInputOutgoingFromAddress } from '@/api/types/request.types'

export default defineComponent({

  components: {
    MailboxVerificationLayout,
    EntitySettingsLoading,
    EmailClientFigure,
  },

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      mailboxName: '',
      isLoading: true,
      isSubmitting: false,
      emailIsPublicDomain: false,
      pollingForOutgoingFromEmailVerification: false,
      pollingFailed: false,
      poller: null as any,
      pollCount: 0,
      pollIntervalInMS: 5000,
      pollMax: 36,
      forwardingFromAddress: '',
      outgoingEmailSettings: {} as IMailboxesOutputOutgoingEmailSettings,
      outgoingFromAddressData: {
        fromName: '',
        fromEmailAddress: '',
      } as IMailboxesInputOutgoingFromAddress,
      postmarkSenderSignatureConfirmationUrl: '',
      postmarkUrlClicked: false,

      stepPercentage: 100,
    }
  },
  head: {
    title: 'Outgoing Email Setup',
  },

  computed: {
    accountID(): number {
      return Number(this.$route.params.accountId)
    },
    mailboxID(): number {
      return Number(this.$route.params.id)
    },
    hasCustomFromAddress(): boolean {
      return this.outgoingEmailSettings.dnsInfo !== null
    },
    disableSubmit(): boolean {
      return this.outgoingFromAddressData.fromName.trim() === ''
    },
    isCustomFromAddressSameAsForwardingAddress(): boolean {
      return (
        this.hasCustomFromAddress &&
        this.outgoingEmailSettings.outgoingFromEmail &&
        this.outgoingEmailSettings.outgoingFromEmail.emailAddress ===
          this.forwardingFromAddress
      )
    },
    hasPostmarkSenderSignatureConfirmationUrl(): boolean {
      return this.postmarkSenderSignatureConfirmationUrl !== ''
    },
  },

  watch: {
    pollCount: {
      handler(newValue) {
        if (newValue >= this.pollMax) {
          this.endPolling()
          this.pollingFailed = true
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    try {
      await Promise.all([
        this.getOutgoingEmailSettings(),
        this.getForwardingFromAddress(),
      ])
      this.mailboxName = this.outgoingEmailSettings.name
      this.outgoingFromAddressData.fromName = this.outgoingEmailSettings.outgoingFromEmail.name
      this.outgoingFromAddressData.fromEmailAddress = this.forwardingFromAddress
    } catch (error) {
      this.$error.emit(error, true)
    } finally {
      this.isLoading = false
    }
  },

  beforeUnmount() {
    this.endPolling()
  },

  methods: {
    goToMailbox() {
      RouteAssistant.goToMailbox(
        this.$router,
        this.accountID,
        this.mailboxID,
        this.mailboxName,
      )
    },
    goToMailboxAssignPeople() {
      RouteAssistant.goToMailboxSettingsAssignPeopleRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    },
    goToMailboxOutgoingEmail() {
      RouteAssistant.goToMailboxSettingsOutgoingEmailRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    },
    async getOutgoingEmailSettings() {
      this.outgoingEmailSettings = await MailboxesAPI.get_OutgoingEmailSettings(
        this.mailboxID,
      )
    },
    async getForwardingFromAddress() {
      try {
        const response = await MailboxesAPI.get_AutoForwarding(this.mailboxID)

        // Assumption: Newly added forwarding address is placed at the end on the backend...
        this.forwardingFromAddress =
          response.listForwardingEmailAddresses[
            response.listForwardingEmailAddresses.length - 1
          ]

        if (!this.forwardingFromAddress) {
          RouteAssistant.goToMailboxVerificationIntroRoute(
            this.$router,
            this.accountID,
            this.mailboxID,
          )
        }
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async setOutgoingEmailAndAwaitVerification() {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.post_OutgoingFromAddress(
          this.mailboxID,
          this.outgoingFromAddressData,
        )
        this.pollingForOutgoingFromEmailVerification = true
        this.pollForOutgoingFromEmailVerification()
      } catch (error) {
        if (error.data.message.includes('public domain')) {
          this.emailIsPublicDomain = true
        } else {
          this.$error.emit(error, true)
        }
      } finally {
        this.isSubmitting = false
      }
    },
    clearPostmarkUrlInfo() {
      this.postmarkSenderSignatureConfirmationUrl = ''
      this.postmarkUrlClicked = false
    },
    endPolling() {
      clearInterval(this.poller)
      this.pollingForOutgoingFromEmailVerification = false
    },
    async pollForOutgoingFromEmailVerification() {
      // Clear just in case there's any rif-raff...
      this.endPolling()
      this.clearPostmarkUrlInfo()
      this.pollCount = 0
      this.poller = setInterval(async () => {
        this.pollCount++
        await this.confirmOutgoingFromEmailVerification()
      }, this.pollIntervalInMS)
      this.pollingForOutgoingFromEmailVerification = true
    },
    async confirmOutgoingFromEmailVerification() {
      try {
        // See if there's a Postmark link to click (which would then let the
        // following confirmation call succeed)
        if (!this.hasPostmarkSenderSignatureConfirmationUrl) {
          await this.retrievePostmarkSenderSignatureConfirmationUrl()
        }
        // If still pending, will throw error...
        await MailboxesAPI.post_ConfirmPendingOutgoingFromAddress(
          this.mailboxID,
        )
        // If passes, can assume email was confirmed...
        const response = await MailboxesAPI.get_OutgoingEmailSettings(
          this.mailboxID,
        )
        if (
          response.outgoingFromEmail.emailAddress === this.forwardingFromAddress
        ) {
          this.outgoingEmailSettings = response
          this.endPolling()
        }
      } catch (error) {
        // Don't emit anything...this is a polling method
      }
    },
    async retrievePostmarkSenderSignatureConfirmationUrl() {
      const response = await MailboxesAPI.get_PostmarkSenderSignatureConfirmationUrl(
        this.mailboxID,
      )

      if (response.url && response.url.length > 0) {
        this.postmarkSenderSignatureConfirmationUrl = response.url
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-verification-outgoing-email {
  .bouncy {
    animation-name: dd-bounce;
    animation-duration: 1s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  }

  &__email-client-figure {
    margin-bottom: 20px;
  }

  &__form {
    padding-top: 20px;
    .dd-button {
      margin-top: 20px;
      width: 100%;
    }
  }

  .public-domain-alert {
    display: flex;
    background-color: $color--red;
    align-items: center;
    padding: 10px;
    border-radius: 8px;
    color: $color--white;

    > i {
      font-size: 24px;
      padding-right: 10px;
      top: -2px;
    }

    > p {
      color: $color--white;
    }
  }

  .postmark-verification {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 20px 0 20px;

    .confirmation-link-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      margin-top: 10px;
    }

    img {
      height: 21px;
    }

    p {
      text-align: center;
    }
  }

  .outgoing-email-options {
    display: flex;
    flex-direction: column;
  }
}
</style>
